import { Button, ConfirmButton, Notice, buttonClass } from '../ui';
import { Link } from '../router/Link';
import { useCopy } from '../strings/useCopy';
import { useSubscriptionHere } from './useSubscription';

/**
 * The sub/unsub control — the channel head and the Watch byline both draw it. Self-contained the way
 * `AddToPlaylist` is: it reads the session and the shared subscription list itself, so a caller only
 * names the channel.
 *
 * Signed out it is a *link* to the sign-in page (which returns here), not a dead button — §8's rule
 * that a gated affordance explains itself. While the list is still loading the button renders
 * disabled rather than guessing: "Subscribe" flashed at someone already subscribed reads as the app
 * having forgotten them.
 */
export function SubscribeButton({ channelId }: { readonly channelId: string | null }) {
  const copy = useCopy().text;
  const words = copy.subscriptions.button;
  const binding = useSubscriptionHere(channelId);

  if (!binding.available) return null;

  if (!binding.signedIn) {
    return (
      <Link
        className={buttonClass({ variant: 'default', size: 'sm' })}
        href={binding.signInHref}
        title={words.signedOutTitle}
      >
        {words.subscribe}
      </Link>
    );
  }

  // Subscribing is one click; *un*subscribing is two — the button arms red first, and a
  // click anywhere else stands down. The same grammar as every remove button in the app.
  if (binding.subscribed === true) {
    return (
      <>
        <ConfirmButton
          variant="default"
          size="sm"
          disabled={binding.busy}
          confirmLabel={copy.shell.confirm}
          confirmTitle={words.unsubscribe}
          onConfirm={binding.toggle}
        >
          {words.unsubscribe}
        </ConfirmButton>
        {binding.problem === null ? null : (
          <Notice tone="caution" live>
            <strong>{binding.problem.title}.</strong> {binding.problem.detail}
          </Notice>
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        disabled={binding.subscribed === null || binding.busy}
        onClick={binding.toggle}
      >
        {words.subscribe}
      </Button>
      {binding.problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{binding.problem.title}.</strong> {binding.problem.detail}
        </Notice>
      )}
    </>
  );
}
